/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactElement, useState } from 'react'
import { TFunction } from 'react-i18next'

import { handleAuth } from '../../../services'
import { useAppSelector } from '../../../store'
import { User } from '../../../types'
import { getCategoryQuery } from '../../../utils'
import { MenuItem } from '../navbar/navBarItems'

import './index.css'

type NavbarProps = {
  menuItems: MenuItem[]
  t: TFunction<'translation', undefined>
  user: User | undefined
  history?: any
  closeDrawer: () => void
}

type SubNavItem = {
  title: string
  items?: { title: string; url: string; id: number }[]
  url: string
}

const DrawerNavSubMenu = (
  t: TFunction<'translation', undefined>,
  subNavItems: SubNavItem[],
  hasHideClassName: boolean,
  index: number,
  parentLink: string,
  handleShopSubItemClick?: (subItem: SubNavItem) => void,
  handlePageRedirection?: (redirectLink: string) => void,
  showViewAll?: boolean,
  totalProducts?: number
): ReactElement => {
  const subMenuClass = !hasHideClassName ? 'drawer-sub-nav-show' : 'drawer-sub-nav-hide'
  return (
    <div key={'sub drawer nav menu ' + index} className={`home-mobile-drawer-nav-sub-menu ${subMenuClass}`}>
      {subNavItems.map((item, index) => {
        return (
          <button
            key={index}
            className="home-mobile-drawer-nav-sub-menu-item"
            onClick={() => {
              if (handleShopSubItemClick) {
                handleShopSubItemClick(item)
              }
            }}
          >
            <span className="drawer-menu-item">{item.title || item}</span>
            {item.items ? <i className="feather-chevron-right"></i> : null}
          </button>
        )
      })}
      {showViewAll ? (
        <button
          className="home-mobile-drawer-nav-sub-menu-item home-mobile-drawer-nav-sub-menu-all heavy"
          onClick={() => {
            if (handlePageRedirection) {
              handlePageRedirection(parentLink)
            }
          }}
        >
          {t('Common_viewAll')} {totalProducts ? `(${totalProducts})` : ''}
        </button>
      ) : null}
    </div>
  )
}

const DrawerNavMenu: FC<NavbarProps> = ({ menuItems, t, user, history, closeDrawer }): ReactElement => {
  const { categories } = useAppSelector((state) => state.categoryReducer)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [subItems, setSubItems] = useState<any[]>([])
  const [navTitles, setNavTitles] = useState<string[]>([])
  const [parentLinks, setParentLinks] = useState<string[]>([])
  const [isBacking, setIsBacking] = useState<boolean>(false)

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.shopDropdown) {
      setSubItems([...subItems, ShopDropData])
      setNavTitles([...navTitles, item.title])
      setParentLinks([...parentLinks, item.href])
      setIsBacking(false)
    } else if (item.dropdownMenu) {
      setSubItems([...subItems, item.dropdownMenu])
      setNavTitles([...navTitles, item.title])
      setParentLinks([...parentLinks, item.href])
      setIsBacking(false)
    } else {
      handlePageRedirection(item.href)
    }
  }

  const getShopDropData = (): SubNavItem[] => {
    return [
      {
        title: t('Categories_featured'),
        url: '/shop?category=featured',
        items: [
          { title: t('Categories_trendingProducts'), url: '/shop?category=trending', id: 1 },
          { title: t('Categories_topDeals'), url: '/shop?category=top deals', id: 2 },
          { title: t('Categories_facultyMostPopular'), url: '/shop?category=faculty trending', id: 3 },
        ],
      },
      ...categories.map((item) => {
        return {
          title: item.name,
          siteCategoryId: item.siteCategoryId,
          url: `/shop?category=` + getCategoryQuery(item.name),
          items: item.producerList
            ? item.producerList.map((p, i) => {
                return {
                  title: p,
                  url:
                    `/shop?category=${getCategoryQuery(item.name)}` +
                    `&categoryId=${item.siteCategoryId}` +
                    `&publisher=${getCategoryQuery(p)}`,
                  id: i + 1,
                }
              })
            : [],
        }
      }),
    ]
  }

  const ShopDropData = getShopDropData()

  const handleShopSubItemClick = (item: SubNavItem): void => {
    if (item.items) {
      setSubItems([...subItems, item.items])
      setNavTitles([...navTitles, item.title])
      setParentLinks([...parentLinks, item.url])
    } else {
      handlePageRedirection(item.url)
    }
  }

  const handlePageRedirection = (redirectLink: string): void => {
    if (history) history.push(redirectLink)
    else window.location.href = redirectLink
    //close the drawer after redirection
    closeDrawer()
  }

  const handleBackCliCk = () => {
    if (!isBacking) {
      setIsBacking(true)
      setTimeout(() => {
        const newSubItems = [...subItems]
        newSubItems.pop()
        const newNavTitles = [...navTitles]
        newNavTitles.pop()
        const newParentLinks = [...parentLinks]
        newParentLinks.pop()
        setSubItems(newSubItems)
        setNavTitles(newNavTitles)
        setParentLinks(newParentLinks)
        setIsBacking(false)
      }, 300)
    }
  }

  const handleNavTitleClick = () => {
    handlePageRedirection(parentLinks[parentLinks.length - 1])
  }

  const navTitle = navTitles[navTitles.length - 1]
  return (
    <div className="home-mobile-drawer-nav">
      <div className="home-mobile-drawer-nav-header">
        {!subItems.length || (subItems.length === 1 && isBacking) ? (
          user ? (
            <a href="/account">
              <button className="home-mobile-drawer-nav-header-sign-in">
                <i className="feather-user"></i>
                <span>
                  {t('Common_greeting')}, {user.profile.firstName || user.username}
                </span>
              </button>
            </a>
          ) : (
            <button className="home-mobile-drawer-nav-header-sign-in" onClick={async () => await handleAuth()}>
              <i className="feather-user"></i>
              <span>{t('Common_SignIn')}</span>
            </button>
          )
        ) : (
          <>
            <button className="home-mobile-drawer-nav-header-back" onClick={handleBackCliCk}>
              <i className="feather-chevron-left"></i>
              <span>{t('Common_back')}</span>
            </button>
            <button className="home-mobile-drawer-nav-header-nav-title heavy" onClick={handleNavTitleClick}>
              {navTitle}
            </button>
          </>
        )}
      </div>
      <div className="home-mobile-drawer-nav-content-container">
        <div className="home-mobile-drawer-nav-menu">
          {subItems.map((item, index) => {
            const hasHideClassName = index === subItems.length - 1 ? isBacking : false
            return DrawerNavSubMenu(
              t,
              item,
              hasHideClassName,
              index,
              parentLinks[parentLinks.length - 1],
              handleShopSubItemClick,
              handlePageRedirection,
              index === 1
            )
          })}
          {menuItems.map((item, index) => {
            return (
              <button key={index} className="home-mobile-drawer-nav-menu-item" onClick={() => handleMenuItemClick(item)}>
                <span className="drawer-menu-item">{item.title}</span>
                {item.dropdownMenu || item.shopDropdown ? <i className="feather-chevron-right"></i> : null}
              </button>
            )
          })}
        </div>
        {user && (
          <a href="/logout">
            <button className="home-mobile-drawer-log-out light">
              <i className="feather-log-out"></i>
              {t('Common_logout')}
            </button>
          </a>
        )}
      </div>
    </div>
  )
}

export default DrawerNavMenu
